import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, RotateCcw, Upload, X } from "lucide-react";
import { useRef, useState } from "react";

import { ModalOverlay } from "@/components/ModalOverlay";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import type {
  PaymentMethodCatalogEntry,
  PaymentMethodColorToken,
  PaymentMethodFlowKind,
  PaymentMethodIconKind,
  PaymentMethodReversalMethod,
} from "@tilt/admin-sdk";

const FLOW_KINDS: PaymentMethodFlowKind[] = [
  "amount_only",
  "check_number",
  "card_terminal",
  "phone_link",
  "embedded_iframe",
  "external_link",
];

const ICON_KINDS: PaymentMethodIconKind[] = [
  "banknote",
  "check",
  "credit_card",
  "bank",
  "phone",
  "iframe",
  "generic",
];

const COLOR_TOKENS: PaymentMethodColorToken[] = ["emerald", "sky", "violet", "amber", "neutral"];

const REVERSAL_METHODS: { value: PaymentMethodReversalMethod; label: string }[] = [
  { value: "void", label: "Void (cash / check)" },
  { value: "return_to_original", label: "Return to original (card / ACH processor)" },
  { value: "cash_return", label: "Cash return (hand back cash)" },
  { value: "non_refundable", label: "Non-refundable (deposits / fees)" },
];

const COLOR_DOT: Record<string, string> = {
  emerald: "bg-emerald-500",
  sky: "bg-sky-500",
  violet: "bg-violet-500",
  amber: "bg-amber-500",
  neutral: "bg-gray-400",
};

const ACCEPTED_MIME = "image/png,image/jpeg,image/svg+xml,image/webp";
const MAX_BYTES = 2 * 1024 * 1024;
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/** Relative luminance per WCAG 2.1. */
function luminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const srgb = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * srgb(r) + 0.7152 * srgb(g) + 0.0722 * srgb(b);
}

/** WCAG contrast ratio (between 1 and 21). */
function contrastRatio(hexA: string, hexB: string): number {
  const la = luminance(hexA);
  const lb = luminance(hexB);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

export function PaymentMethodsCatalogTab(): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<PaymentMethodCatalogEntry | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["payment-method-catalog"],
    queryFn: () => apiClient.listPaymentMethodCatalog(),
    staleTime: 30_000,
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["payment-method-catalog"] });

  const reset = useMutation({
    mutationFn: () => apiClient.resetPaymentMethodCatalogDefaults(),
    onSuccess: () => {
      setResetError(null);
      invalidate();
    },
    onError: (err: unknown) => setResetError(describe(err)),
  });

  const methods = query.data?.payment_methods ?? [];

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Platform-global display and behaviour metadata for every payment method. Super-admins can
          edit labels, icons, colors, and flow kinds here. Changes apply across all partners
          immediately.
        </p>
        <button
          type="button"
          onClick={() => {
            if (confirm("Reset all payment methods to platform defaults? This cannot be undone.")) {
              reset.mutate();
            }
          }}
          disabled={reset.isPending}
          className="flex items-center gap-1.5 rounded-md border border-amber-400/40 bg-amber-50 px-3 py-1.5 text-sm text-amber-900 hover:bg-amber-100 disabled:opacity-50 dark:bg-amber-900/20 dark:text-amber-200"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset to defaults
        </button>
      </div>

      {resetError && (
        <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">
          {resetError}
        </div>
      )}

      {query.isLoading && <p className="text-sm text-muted-foreground">Loading...</p>}

      {!query.isLoading && methods.length === 0 && (
        <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-center text-sm text-muted-foreground">
          No payment methods found. The V0114 migration should seed 6 default rows.
        </div>
      )}

      <div className="overflow-x-auto rounded-md border bg-card">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/40 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2">Method</th>
              <th className="px-3 py-2">Label</th>
              <th className="px-3 py-2">Order</th>
              <th className="px-3 py-2">Flow</th>
              <th className="px-3 py-2">Icon</th>
              <th className="px-3 py-2">Color</th>
              <th className="px-3 py-2">Reversal</th>
              <th className="px-3 py-2">Flags</th>
              <th className="px-3 py-2 sr-only">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {methods.map((m) => (
              <tr key={m.feature_path} className="hover:bg-muted/20">
                <td className="px-3 py-2">
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {m.feature_path.split(".").pop()}
                  </span>
                  {m.card_group_key && (
                    <span className="ml-1.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                      {m.card_group_key}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 font-medium">{m.display_label_en}</td>
                <td className="px-3 py-2 tabular-nums text-muted-foreground">{m.display_order}</td>
                <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">
                  {m.flow_kind}
                </td>
                <td className="px-3 py-2">
                  {m.icon_light_url ? (
                    <span className="flex items-center gap-1.5">
                      <img
                        src={m.icon_light_url}
                        alt=""
                        className="h-4 w-4 rounded-sm object-contain"
                      />
                      <span className="text-[10px] text-muted-foreground">custom</span>
                    </span>
                  ) : (
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {m.icon_kind}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {m.custom_color ? (
                    <span className="flex items-center gap-1.5">
                      <span
                        className="h-2.5 w-2.5 rounded-full ring-1 ring-black/10"
                        style={{ backgroundColor: m.custom_color }}
                        aria-hidden="true"
                      />
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {m.custom_color}
                      </span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      <span
                        className={cn(
                          "h-2.5 w-2.5 rounded-full",
                          COLOR_DOT[m.color_token] ?? "bg-gray-400",
                        )}
                        aria-hidden="true"
                      />
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {m.color_token}
                      </span>
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {m.reversal_method ?? (m.is_voidable ? "void" : "return_to_original")}
                  </span>
                </td>
                <td className="px-3 py-2 text-[10px] text-muted-foreground">
                  {[
                    m.requires_phone ? "phone" : null,
                    m.requires_check_number ? "check#" : null,
                    m.requires_device_picker ? "device" : null,
                  ]
                    .filter(Boolean)
                    .join(", ") || "-"}
                </td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => setEditing(m)}
                    aria-label={`Edit ${m.display_label_en}`}
                    className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
                  >
                    <Pencil className="h-3 w-3" />
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <PaymentMethodEditDialog
          method={editing}
          onClose={() => setEditing(null)}
          onMutated={() => {
            setEditing(null);
            invalidate();
          }}
        />
      )}
    </section>
  );
}

function PaymentMethodEditDialog(props: {
  method: PaymentMethodCatalogEntry;
  onClose: () => void;
  onMutated: () => void;
}): JSX.Element {
  const { method } = props;
  const [displayLabel, setDisplayLabel] = useState(method.display_label_en);
  const [localeKey, setLocaleKey] = useState(method.locale_key ?? "");
  const [displayOrder, setDisplayOrder] = useState(String(method.display_order));
  const [reversalMethod, setReversalMethod] = useState<PaymentMethodReversalMethod>(
    method.reversal_method ?? (method.is_voidable ? "void" : "return_to_original"),
  );
  const [flowKind, setFlowKind] = useState(method.flow_kind);
  const [iconKind, setIconKind] = useState(method.icon_kind);
  const [colorToken, setColorToken] = useState(method.color_token);
  const [requiresPhone, setRequiresPhone] = useState(method.requires_phone);
  const [requiresCheckNumber, setRequiresCheckNumber] = useState(method.requires_check_number);
  const [requiresDevicePicker, setRequiresDevicePicker] = useState(method.requires_device_picker);
  const [cardGroupKey, setCardGroupKey] = useState(method.card_group_key ?? "");
  const [iconLightUrl, setIconLightUrl] = useState<string | null>(method.icon_light_url);
  const [iconDarkUrl, setIconDarkUrl] = useState<string | null>(method.icon_dark_url);
  const [customColor, setCustomColor] = useState(method.custom_color ?? "");
  const [uploadingLight, setUploadingLight] = useState(false);
  const [uploadingDark, setUploadingDark] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lightInputRef = useRef<HTMLInputElement>(null);
  const darkInputRef = useRef<HTMLInputElement>(null);

  const hexValid = customColor === "" || HEX_RE.test(customColor);
  const contrastWarn =
    hexValid && customColor !== ""
      ? contrastRatio(customColor, "#ffffff") < 3 && contrastRatio(customColor, "#000000") < 3
      : false;

  async function handleIconUpload(file: File, variant: "light" | "dark"): Promise<void> {
    if (file.size > MAX_BYTES) {
      setUploadError(`File too large (max 2 MB).`);
      return;
    }
    const ct = file.type as "image/png" | "image/jpeg" | "image/svg+xml" | "image/webp";
    if (!["image/png", "image/jpeg", "image/svg+xml", "image/webp"].includes(ct)) {
      setUploadError("Unsupported file type. Use PNG, JPEG, SVG, or WebP.");
      return;
    }
    setUploadError(null);
    if (variant === "light") setUploadingLight(true);
    else setUploadingDark(true);
    try {
      const { put_url, public_url } = await apiClient.requestPaymentMethodIconUpload({
        content_type: ct,
      });
      await fetch(put_url, {
        method: "PUT",
        headers: { "Content-Type": ct },
        body: file,
      });
      if (variant === "light") setIconLightUrl(public_url);
      else setIconDarkUrl(public_url);
    } catch (err) {
      setUploadError(describe(err));
    } finally {
      if (variant === "light") setUploadingLight(false);
      else setUploadingDark(false);
    }
  }

  const save = useMutation({
    mutationFn: () =>
      apiClient.updatePaymentMethod(method.feature_path, {
        display_label_en: displayLabel.trim(),
        locale_key: localeKey.trim() || null,
        display_order: parseInt(displayOrder, 10) || method.display_order,
        is_voidable: reversalMethod === "void",
        reversal_method: reversalMethod,
        flow_kind: flowKind,
        icon_kind: iconKind,
        color_token: colorToken,
        requires_phone: requiresPhone,
        requires_check_number: requiresCheckNumber,
        requires_device_picker: requiresDevicePicker,
        card_group_key: cardGroupKey.trim() || null,
        icon_light_url: iconLightUrl,
        icon_dark_url: iconDarkUrl,
        custom_color: customColor.trim() || null,
      }),
    onSuccess: props.onMutated,
    onError: (err: unknown) => setError(describe(err)),
  });

  return (
    <ModalOverlay ariaLabel={`Edit ${method.display_label_en}`} onClose={props.onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="w-full max-w-lg space-y-4 rounded-t-xl border bg-card p-5 shadow-lg sm:rounded-xl"
      >
        <div>
          <h2 className="text-sm font-semibold">Edit {method.display_label_en}</h2>
          <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
            {method.feature_path} (immutable)
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <EditField label="Display label (EN)" id="pm-label">
            <input
              id="pm-label"
              required
              value={displayLabel}
              onChange={(e) => setDisplayLabel(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </EditField>
          <EditField label="Locale key" id="pm-locale" hint="e.g. method.cash (optional)">
            <input
              id="pm-locale"
              value={localeKey}
              onChange={(e) => setLocaleKey(e.target.value)}
              placeholder="method.cash"
              className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
            />
          </EditField>
          <EditField label="Display order" id="pm-order">
            <input
              id="pm-order"
              type="number"
              min={1}
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </EditField>
          <EditField
            label="Card group key"
            id="pm-group"
            hint="Groups card-family tiles (e.g. card)"
          >
            <input
              id="pm-group"
              value={cardGroupKey}
              onChange={(e) => setCardGroupKey(e.target.value)}
              placeholder="card"
              className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
            />
          </EditField>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <EditField label="Flow kind" id="pm-flow">
            <select
              id="pm-flow"
              value={flowKind}
              onChange={(e) => setFlowKind(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              {FLOW_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </EditField>

          {/* Icon kind dropdown + custom upload slots */}
          <EditField label="Icon kind" id="pm-icon" hint="Or upload a custom icon below">
            <select
              id="pm-icon"
              value={iconKind}
              onChange={(e) => setIconKind(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              {ICON_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </EditField>

          {/* Color token dropdown + hex picker */}
          <EditField label="Color token" id="pm-color" hint="Or set a custom hex color below">
            <select
              id="pm-color"
              value={colorToken}
              onChange={(e) => setColorToken(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              {COLOR_TOKENS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </EditField>
        </div>

        {/* Custom icon upload slots */}
        <div className="space-y-2 rounded-md border bg-muted/20 p-3">
          <p className="text-[11px] font-medium text-muted-foreground">
            Custom icon (overrides icon kind when set)
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <IconUploadSlot
              label="Light-mode icon"
              url={iconLightUrl}
              uploading={uploadingLight}
              inputRef={lightInputRef}
              onFile={(f) => void handleIconUpload(f, "light")}
              onClear={() => setIconLightUrl(null)}
            />
            <IconUploadSlot
              label="Dark-mode icon"
              url={iconDarkUrl}
              uploading={uploadingDark}
              inputRef={darkInputRef}
              onFile={(f) => void handleIconUpload(f, "dark")}
              onClear={() => setIconDarkUrl(null)}
            />
          </div>
          {uploadError && <p className="text-[11px] text-destructive">{uploadError}</p>}
        </div>

        {/* Custom hex color */}
        <div className="space-y-1.5 rounded-md border bg-muted/20 p-3">
          <p className="text-[11px] font-medium text-muted-foreground">
            Custom color (overrides color token when set)
          </p>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={customColor || "#888888"}
              onChange={(e) => setCustomColor(e.target.value)}
              className="h-8 w-10 cursor-pointer rounded border bg-background p-0.5"
              aria-label="Color picker"
            />
            <input
              type="text"
              value={customColor}
              onChange={(e) => setCustomColor(e.target.value)}
              placeholder="#rrggbb"
              maxLength={7}
              className={cn(
                "w-28 rounded-md border bg-background px-2 py-1.5 font-mono text-sm",
                !hexValid && "border-destructive",
              )}
              aria-label="Hex color value"
            />
            {customColor && (
              <button
                type="button"
                onClick={() => setCustomColor("")}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Clear custom color"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {customColor && hexValid && (
              <span
                className="h-5 w-5 rounded-full ring-1 ring-black/10"
                style={{ backgroundColor: customColor }}
                aria-hidden="true"
              />
            )}
          </div>
          {!hexValid && (
            <p className="text-[11px] text-destructive">
              Must be a 6-digit hex color, e.g. #ff6600
            </p>
          )}
          {contrastWarn && (
            <p className="text-[11px] text-amber-700 dark:text-amber-400">
              Low contrast: this color may be hard to read on both light and dark backgrounds (WCAG
              3:1).
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <EditField label="Reversal method" id="pm-reversal">
            <select
              id="pm-reversal"
              value={reversalMethod}
              onChange={(e) => setReversalMethod(e.target.value as PaymentMethodReversalMethod)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              {REVERSAL_METHODS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </EditField>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={requiresPhone}
              onChange={(e) => setRequiresPhone(e.target.checked)}
              className="h-3.5 w-3.5 accent-primary"
            />
            Requires phone
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={requiresCheckNumber}
              onChange={(e) => setRequiresCheckNumber(e.target.checked)}
              className="h-3.5 w-3.5 accent-primary"
            />
            Requires check number
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={requiresDevicePicker}
              onChange={(e) => setRequiresDevicePicker(e.target.checked)}
              className="h-3.5 w-3.5 accent-primary"
            />
            Requires device picker
          </label>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={
              !displayLabel.trim() || !hexValid || save.isPending || uploadingLight || uploadingDark
            }
            className="flex-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {save.isPending ? "Saving..." : "Save changes"}
          </button>
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

function IconUploadSlot(props: {
  label: string;
  url: string | null;
  uploading: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onFile: (f: File) => void;
  onClear: () => void;
}): JSX.Element {
  return (
    <div className="space-y-1">
      <p className="text-[11px] text-muted-foreground">{props.label}</p>
      <div className="flex items-center gap-2">
        {props.url ? (
          <>
            <img
              src={props.url}
              alt=""
              className="h-8 w-8 rounded-sm border object-contain bg-white p-0.5"
            />
            <button
              type="button"
              onClick={props.onClear}
              className="text-muted-foreground hover:text-destructive"
              aria-label={`Remove ${props.label}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </>
        ) : (
          <button
            type="button"
            disabled={props.uploading}
            onClick={() => props.inputRef.current?.click()}
            className="flex items-center gap-1.5 rounded-md border bg-background px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted disabled:opacity-50"
          >
            <Upload className="h-3 w-3" />
            {props.uploading ? "Uploading..." : "Upload"}
          </button>
        )}
      </div>
      <input
        ref={props.inputRef}
        type="file"
        accept={ACCEPTED_MIME}
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) props.onFile(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}

function EditField(props: {
  label: string;
  id: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={props.id} className="mb-1 block text-xs font-medium text-muted-foreground">
        {props.label}
      </label>
      {props.children}
      {props.hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{props.hint}</p>}
    </div>
  );
}
